/**
 * AeManual — /#/manuals/ae
 * Long-scroll, well-typeset HTML manual mirroring the AE markdown source.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 *
 * W66.X2 rewrites:
 *   - Added Chapter 4: Today Command Center (/#/today) — 7-block walkthrough
 *   - Added Chapter 5: Top-10 Sales Plan workbook (/#/sales-plan)
 *   - Removed all /#/sales-path references (route retired W54)
 *   - Removed PROBABLE forecast bucket (retired W63/W64)
 *   - Fixed M pillar: CP3=Budget, CP4=Timeline, CP5=Strategic Fit
 *   - Fixed Stage 3 body copy (Timeline, not fit)
 *   - Fixed "approval gates" → "approval checkpoints" (CP6 table)
 *   - Fixed "People-mapping rule" → "Contact-mapping rule"
 *   - Fixed Composite score / M-PACT conflation in glossary
 *   - Renumbered chapters to accommodate new sections
 */

import ManualLayout, {
  ManualSectionHeading,
  Callout,
  ManualImage,
  StepList,
  BulletList,
  ManualPara,
  ManualTable,
  type ManualSection,
} from "@/components/manuals/ManualLayout";

import shot01 from "@/assets/manuals/ae/01-dashboard-ae.png";
import shot02 from "@/assets/manuals/ae/02-ae-account-list.png";
import shot03 from "@/assets/manuals/ae/03-ae-account-detail.png";
import shot04 from "@/assets/manuals/ae/04-ae-question-bank.png";
import shot06 from "@/assets/manuals/ae/06-ae-plan-vs-actual.png";

const SECTIONS: ManualSection[] = [
  { id: "welcome",       label: "Welcome and how to use this manual" },
  { id: "role",          label: "The role: what an AE owns in M-PACT" },
  { id: "login",         label: "Logging in and navigating" },
  { id: "today",         label: "Today Command Center: your morning surface" },
  { id: "sales-plan",    label: "Top-10 Sales Plan workbook" },
  { id: "top10",         label: "Building your Top 10 sales plan" },
  { id: "working",       label: "Working an account: CP3-CP10 with the question bank" },
  { id: "decision-map",  label: "The decision-maker map" },
  { id: "plans-audits",  label: "Plans, audits, and the Attilius scoring model" },
  { id: "plan-vs-actual", label: "Plan vs actual: pipeline coverage and win-rate" },
  { id: "glossary",      label: "Glossary, gotchas, and closing note" },
];

const BLACK = "#0B0B0F";

export default function AeManual() {
  return (
    <ManualLayout
      role="AE"
      title="Attilius Operating Manual — AE"
      meta="AE role · v1.1 · June 2026"
      sections={SECTIONS}
    >
      {/* ── Chapter 1 — Welcome ──────────────────────────────────────────────── */}
      <ManualSectionHeading id="welcome" num={1} title="Welcome and how to use this manual" />

      <ManualPara>
        This manual covers one role: the AE. It does not cover BDR cadence management, Marketing
        hook approval, or Manager pipeline reviews. Those have their own manuals. This one is yours.
      </ManualPara>

      <ManualPara>
        Read it once from beginning to end before your first session on the platform. Then use it as
        a reference — the chapter structure mirrors the Attilius navigation.
      </ManualPara>

      <BulletList
        items={[
          <span>M-PACT is the operating model. You work inside it. You do not invent a parallel process alongside it.</span>,
          <span>The 10 checkpoints (CP1 through CP10) are the qualification spine. As an AE, your direct scope is CP3 through CP9. Everything the BDR gathered in CP1 and CP2 is your foundation.</span>,
          <span>Attilius is the platform. The two marks are distinct: the elephant is the platform mark, the helix is the diagnostic mark.</span>,
          <span>Manufacturing is the only context here. Contacts are VP Operations, Director of Procurement, VP Engineering, Supply Chain directors, Quality leads, and Plant Managers at Tier 1 OEMs and first-tier suppliers.</span>,
        ]}
      />

      {/* ── Chapter 2 — Role ─────────────────────────────────────────────────── */}
      <ManualSectionHeading id="role" num={2} title="The role: what an AE owns inside M-PACT" />

      <ManualPara>
        The AE owns the middle and close of the funnel. You receive qualified prospects from the BDR
        and carry them from CP3 through CP9 to a closed deal. You are judged on three outcomes:
        quota attainment, pipeline coverage (3x minimum; 5x is healthy), and checkpoint progression
        (every account must show documented forward movement week over week).
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M — Methodology.</strong> Own CP3 through CP9. CP3 — Budget: real money confirmed — named source, named owner, validated line item. CP4 — Timeline: decision window and implementation window both confirmed as real, with a firm or formal date the buyer has stated. CP5 — Strategic Fit: confirmed fit for this customer's industry, size, and operating profile. CP6: decision-maker access and sign-off map. CP7: org structure and power base understood. CP8: competitive landscape understood and positioning confirmed in the buyer's context. CP9: compliance requirements, payment terms, and approval path clear.</span>,
          <span><strong style={{ color: BLACK }}>P — Process / Prioritization.</strong> Run each account through the checkpoint sequence in order. Do not jump from CP3 to CP6 because the advocate seems enthusiastic. An account cannot be in Commit without CP5 answered.</span>,
          <span><strong style={{ color: BLACK }}>A — Accountability.</strong> Log activity daily. Three blank days in a row is a visible signal to your manager.</span>,
          <span><strong style={{ color: BLACK }}>C — Cadence / Coaching.</strong> Attend the weekly pipeline review. Every account you bring must have a checkpoint status and a documented next action.</span>,
          <span><strong style={{ color: BLACK }}>T — Timing / Timeline management.</strong> Know the firm or formal timeline for every account. A deal with no firm timeline is not a deal.</span>,
        ]}
      />

      <ManualTable
        headers={["CP", "Name", "BDR gave you", "AE owns"]}
        rows={[
          ["CP1", "Account Profile", "Named project, stated pain", "Deepen and confirm"],
          ["CP2", "Compelling Pain", "Pain in prospect's own words", "Quantify, map to business impact"],
          ["CP3", "Budget", "Context only if volunteered", "Validate named budget source and amount"],
          ["CP4", "Timeline", "Context only if volunteered", "Confirm decision + implementation window; confirm firm or formal date"],
          ["CP5", "Strategic Fit", "Name of one project lead", "Confirm fit for this customer's industry, size, and operating profile"],
          ["CP6", "Solution Fit", "Name of one contact", "Confirm technical evaluation and financial approval checkpoints"],
          ["CP7", "Competitive Landscape", "Names heard", "Full competitive assessment"],
          ["CP8", "Decision Process", "One named contact", "Map full stakeholder landscape"],
          ["CP9", "Risk and Stakeholder Alignment", "Not in scope", "Compliance requirements, payment terms, approval path"],
          ["CP10", "Commitment to Buy", "Not in scope", "MSA, redlines, paper path open"],
        ]}
      />

      {/* ── Chapter 3 — Login ────────────────────────────────────────────────── */}
      <ManualSectionHeading id="login" num={3} title="Logging in and navigating the platform" />

      <ManualPara>
        Route — <code>https://aip.mi6group.com</code>. The top navigation gives you access to:
        Daily, Overview, Plan vs Actual, Leaderboard, Question Bank, Methodology, Templates,
        Activity Log, Cadence, Opportunity Calculator, and Manual.
      </ManualPara>

      <ManualTable
        headers={["Screen", "Route"]}
        rows={[
          ["Today Command Center", "/#/today"],
          ["Sales Plan workbook", "/#/sales-plan"],
          ["Accounts list", "/#/accounts"],
          ["Account detail", "/#/account/:id"],
          ["Question Bank", "/#/question-bank"],
          ["Methodology Workspace", "/#/methodology"],
          ["Plan vs Actual", "/#/plan-vs-actual"],
          ["Activity Log", "/#/activity-log"],
          ["Templates", "/#/templates"],
        ]}
      />

      <ManualPara>
        If you are working in a Manager login, use the VIEW AS pill to switch to the AE view. Each
        role sees a different navigation layout and different dashboard tiles.
      </ManualPara>

      {/* ── Chapter 4 — Today Command Center ────────────────────────────────── */}
      <ManualSectionHeading id="today" num={4} title="Today Command Center: your morning surface" />

      <ManualPara>
        Route — <code>/#/today</code>. This is the first surface you open every morning. The Today
        Command Center is a 7-block layout built around Bryan K. Short's approved daily operating
        spec. It replaces the legacy dashboard as the AE's primary daily surface.
      </ManualPara>

      <ManualImage
        src={shot01}
        alt="AE Today Command Center — 7-block layout"
        caption="Live screen: /#/today — AE Today Command Center (W50 V2, 7-block layout)"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The 7 blocks — in order
      </h3>

      <StepList
        steps={[
          <span><strong>Header — AeCommandHeader.</strong> Your name, role, date, and annual quota strip. The quota strip shows Closed YTD, Weighted Pipeline, Gap to Quota, and Coverage Ratio at a glance. Read these four numbers before anything else.</span>,
          <span><strong>Block A — First Hour Directive.</strong> The single highest-priority Top-10 account and the next checkpoint question you need to answer on it. This is your first call of the day. The account is surfaced by M-PACT rank — the one where a CP answer would have the greatest impact on the deal.</span>,
          <span><strong>Block B — Top-10 Account Table.</strong> All accounts flagged is_top_10=true, ranked by M-PACT score. Each row shows rank, account name, composite score, next checkpoint, days stalled, and forecast bucket. Red rows are stalled. Scan this table before your first meeting.</span>,
          <span><strong>Block C — Cadence Queue V2.</strong> All cadence actions due today and overdue, with type pills (Call / Email / LinkedIn) and a Top-10 badge for any touch tied to a Top-10 account. Overdue actions appear first. Clear overdue before starting new-day steps.</span>,
          <span><strong>Block D — Gap Math Strip.</strong> Quota coverage math: Quota Target, Closed Won YTD, Commit pipeline, Best Case pipeline, Gap to Quota, Weighted Pipeline, and Coverage Ratio. Target is 5x coverage in early quarter, maintaining 3x through late quarter.</span>,
          <span><strong>Block E — Yesterday's Wins.</strong> CP answers recorded yesterday and contacts added yesterday, across all your accounts. Use this to confirm your activity from the prior day was captured correctly.</span>,
          <span><strong>Block F — Coaching Inbox V2.</strong> Manager notes and Attilius system prompts merged into a single feed. Manager notes appear with a distinct label. System prompts are pattern-based — they read your logged activity and checkpoint progression, not your personality.</span>,
          <span><strong>Block G — Quick Jump Grid.</strong> Seven tiles for fast navigation: Galaxy, Command View, Sales Plan, Activity Audit, Question Bank, Methodology, Account List. Use this to move between surfaces without returning to the top nav.</span>,
        ]}
      />

      <Callout>
        <strong style={{ color: BLACK }}>Morning sequence.</strong> Open <code>/#/today</code>.
        Read the Header quota strip. Read Block A (First Hour Directive) — that is your first call.
        Scan Block C for overdue cadence items. Clear overdue before starting new steps. Open the
        Sales Plan at <code>/#/sales-plan</code> to confirm your Top-10 status is current.
      </Callout>

      {/* ── Chapter 5 — Sales Plan Workbook ─────────────────────────────────── */}
      <ManualSectionHeading id="sales-plan" num={5} title="Top-10 Sales Plan workbook" />

      <ManualPara>
        Route — <code>/#/sales-plan</code>. The Sales Plan workbook is the second surface in the
        AE morning sequence. It shows your named Top-10 accounts in a two-pane layout: a left rail
        account list and a right-pane plan detail. This is the W66.W6 workbook — the Stellantis
        account is fully populated as a reference.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Left rail — account list
      </h3>

      <ManualPara>
        The left rail lists all 10 named accounts with posture pills. Each pill shows the forecast
        bucket color for that account (COMMIT, UPSIDE, OMIT, or WON). Placeholder accounts render
        as gray until they are seeded with plan data. Click any account name to load its plan detail
        in the right pane.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Right pane — Plan Detail View (7 sections)
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Header.</strong> Account name, posture pill, composite score, and quick-access link to the full account detail at <code>/#/account/:id</code>.</span>,
          <span><strong style={{ color: BLACK }}>ICP Fit.</strong> Ideal Customer Profile assessment — industry, company size, and operating profile match. Sourced from CP5 — Strategic Fit answers.</span>,
          <span><strong style={{ color: BLACK }}>Intel.</strong> Key intelligence gathered on this account: named pain, confirmed budget context, and any competitive signals. Sourced from CP2 and CP3 answers.</span>,
          <span><strong style={{ color: BLACK }}>Relationship and Entry.</strong> Named contacts, their roles, and how the relationship was established. Entry point and first conversation date.</span>,
          <span><strong style={{ color: BLACK }}>Persona Triad.</strong> The three key personas in the buying process for this account: economic buyer, technical evaluator, and user champion. Each persona row shows name, title, and relationship posture.</span>,
          <span><strong style={{ color: BLACK }}>Checkpoint Snapshot.</strong> The 10-checkpoint grid for this account — green, orange, red, or gray for each CP. This is a read-only snapshot; edit CP answers at <code>/#/account/:id</code> in the Opportunity DNA tab.</span>,
          <span><strong style={{ color: BLACK }}>Cadence Bundle.</strong> The active cadence touches for this account. Shows step type, due date, and assigned BDR if the account is still in BDR cadence.</span>,
        ]}
      />

      <ManualPara>
        The page has two tabs. <strong>Top-10 Accounts</strong> is the default and is visible to all
        authenticated roles. <strong>Quota Workbook</strong> is visible to Manager and Executive roles
        only and shows team quota allocation across the Top-10 plan.
      </ManualPara>

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Read-only surface.</strong> The Sales Plan workbook is a
        read-only proof-of-concept surface. To update checkpoint answers, open the account detail at{" "}
        <code>/#/account/:id</code> and edit the Opportunity DNA tab directly.
      </Callout>

      {/* ── Chapter 6 — Top 10 ───────────────────────────────────────────────── */}
      <ManualSectionHeading id="top10" num={6} title="Building your Top 10 sales plan: Bryan K. Short's framework in Attilius" />

      <ManualPara>
        Route — <code>/#/sales-plan</code> for the workbook view; <code>/#/accounts</code> for the
        full account list view; <code>/#/methodology</code> for the checkpoint framework reference.
        This chapter is the most important for an AE. Bryan K. Short's Top 10 framework is the
        operating model that governs how Mi6 AEs build and manage their annual plan.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The four framework components
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Named accounts.</strong> You know the ten accounts that will make your year. Not twenty. Not a spreadsheet of sixty. Ten accounts that are real, qualified, and in your territory.</span>,
          <span><strong style={{ color: BLACK }}>Quarterly milestones.</strong> For each account, you know which checkpoint it needs to reach by end of each quarter to stay on track for a close.</span>,
          <span><strong style={{ color: BLACK }}>Firm timeline under CP4 — Timeline.</strong> Every account in your Top 10 has a confirmed decision window and a firm or formal timeline — a date the buyer has committed to. It lives under CP4 — Timeline.</span>,
          <span><strong style={{ color: BLACK }}>Plan vs actual discipline.</strong> Every week, compare where each account is against where you said it would be.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Step by step: building your Top 10 in Attilius
      </h3>

      <StepList
        steps={[
          <span><strong>Open the Accounts list at <code>/#/accounts</code>.</strong> Apply filters: Stage = Qualified Opp and above, Composite score = 40+. Select the 10 accounts that represent your year.</span>,
          <span><strong>For each Top 10 account, open the detail and confirm:</strong> CP1 and CP2 are green (BDR answered them). CP3 — Budget is in progress or green. Deal Value is entered. Expected Close date is set.</span>,
          <span><strong>Set quarterly milestones.</strong> Write the milestone plan in the account's Plan tab. By end of Q2, reach CP___. By end of Q3, reach CP___. For a Q4 close, CP9 must be confirmed by end of September.</span>,
          <span><strong>Lock the firm timeline under CP4 — Timeline.</strong> Navigate to CP4. Answer: What is the specific date by which this prospect must decide? What happens if they miss that date? Is this set by the prospect's internal calendar or an external event? Has the prospect stated this date explicitly?</span>,
          <span><strong>Set the forecast bucket</strong> with the milestone plan and firm timeline confirmed (see bucket table below).</span>,
          <span><strong>Review your Top 10 every Monday:</strong> Open each account. Read "Where We're Stuck." Confirm the next scheduled action exists. Check if the forecast bucket is still accurate. Confirm the firm timeline is still real.</span>,
        ]}
      />

      <ManualTable
        headers={["Bucket", "Weight", "When to use"]}
        rows={[
          ["WON", "100%", "Contract signed."],
          ["COMMIT", "90%", "CP4 — Timeline and CP5 — Strategic Fit confirmed. Firm timeline real and prospect-stated. Decision criteria clear. Paper process open. You have committed this to your manager verbally."],
          ["UPSIDE", "60%", "CP3 — Budget complete. Budget context exists but not validated. Firm timeline inferred, not stated."],
          ["OMIT", "0%", "Account in pipeline for tracking purposes but not expected to close this period."],
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Hard rule.</strong> Do not move an account into Commit
        until CP4 — Timeline is answered with a documented firm timeline.
      </Callout>

      {/* ── Chapter 7 — Working an Account ──────────────────────────────────── */}
      <ManualSectionHeading id="working" num={7} title="Working an account: walking it through CP3-CP10 with the question bank" />

      <ManualImage
        src={shot02}
        alt="Accounts list — pipeline view with composite scores and stage"
        caption="Live screen: /#/accounts — pipeline list with composite scores and stage"
      />

      <ManualImage
        src={shot03}
        alt="Account detail with CP coverage grid and coaching card"
        caption="Live screen: /#/account/:id — account detail with CP coverage grid and coaching card"
      />

      <ManualImage
        src={shot04}
        alt="M-PACT Question Bank — 190 questions across CP1-CP10"
        caption="Live screen: /#/question-bank — 190 questions across CP1-CP10, filterable by Checkpoint, pillar, and role"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The lifecycle of an account — AE scope
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Stage 1: Receive the handoff.</strong> Read the activity log. Read the CP1 and CP2 answers verbatim in Opportunity DNA. Open the Question Bank at <code>/#/question-bank</code>. Filter to CP3 — Budget. Review the pain the BDR surfaced and prepare to quantify it and confirm a named budget source.</span>,
          <span><strong style={{ color: BLACK }}>Stage 2: Execute CP3 — Budget.</strong> Confirm a named budget source and a budget owner. A budget that is "probably in the capital plan" is not a confirmed budget. Mark each question Green, Orange, or Red in the Opportunity DNA tab. Always enter the prospect's actual language in the notes field.</span>,
          <span><strong style={{ color: BLACK }}>Stage 3: Advance to CP4 — Timeline.</strong> Confirm the decision window and the implementation window. A CP4 — Timeline green means a prospect-stated firm date exists, the implementation window is realistic, and both have been documented. Orange is not green.</span>,
          <span><strong style={{ color: BLACK }}>Stage 4: Surface CP5 — Strategic Fit.</strong> Confirm credible fit for this customer's industry, size, and operating profile. A CP5 — Strategic Fit green means industry fit, size fit, and operating profile fit are all confirmed.</span>,
          <span><strong style={{ color: BLACK }}>Stage 5: Confirm CP6 — Solution Fit.</strong> Name every person who must sign: financial sign-off authority, technical authority, security and legal. A sign-off map with question marks is not a sign-off map.</span>,
          <span><strong style={{ color: BLACK }}>Stage 6: Map CP7 — Competitive Landscape.</strong> Named competitor, internal build, or status quo. Build a competitive narrative grounded in this account's specific pain, not a generic pitch.</span>,
          <span><strong style={{ color: BLACK }}>Stage 7: Assess CP8 — Decision Process.</strong> See Chapter 8 for the full decision-maker mapping framework.</span>,
          <span><strong style={{ color: BLACK }}>Stage 8: Confirm CP9 — Risk and Stakeholder Alignment.</strong> IT internal compliance requirements and industry-specific compliance requirements. Both must be met before legal can close. Do not surprise procurement.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Using the Question Bank for discovery preparation
      </h3>

      <StepList
        steps={[
          <span>Open <code>/#/question-bank</code>.</span>,
          <span>Filter to the checkpoint you are targeting in this meeting.</span>,
          <span>Read the coaching notes under each question — click "COACHING + ANSWERS" to expand.</span>,
          <span>Select two or three questions to anchor the conversation. You are not running a questionnaire — you are preparing the pivots that will get the information you need.</span>,
          <span>After the call, return to the account detail and update the Opportunity DNA answers with what you learned.</span>,
        ]}
      />

      {/* ── Chapter 8 — Decision-Maker Map ───────────────────────────────────── */}
      <ManualSectionHeading id="decision-map" num={8} title="The decision-maker map: advocate / supporter / neutral / detractor" />

      <ManualPara>
        Route — <code>/#/account/:id</code> (tab: Decision Tree or Opportunity DNA, section:
        Decision Makers). The decision-maker map is CP8 — Decision Process. Its purpose is to give
        you a precise picture of the buying team's composition.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Advocate.</strong> The person who wants the deal to happen and will actively sell on your behalf when you are not in the room. You cannot be in Commit without a confirmed advocate. Characteristics: firsthand experience of the pain, organizational credibility, volunteers internal decision process information, connects you to other stakeholders unprompted.</span>,
          <span><strong style={{ color: BLACK }}>Supporter.</strong> A member of the buying team who favors the project and will vote yes, but will not actively campaign for it. Keep supporters informed so they are not surprised by anything in procurement or legal.</span>,
          <span><strong style={{ color: BLACK }}>Neutral.</strong> A stakeholder who has not yet formed a view. Conversion target. Ask: what specific concern has not been addressed that would convert them to a supporter?</span>,
          <span><strong style={{ color: BLACK }}>Detractor.</strong> A stakeholder against the project. Do not route around them — understand them. Common profiles: IT leader concerned about integration, procurement lead with a preferred vendor, operations manager who had a poor experience with a similar platform, CFO skeptical of ROI.</span>,
        ]}
      />

      <StepList
        steps={[
          <span>Open the account detail and click the Decision Tree tab.</span>,
          <span>For each named stakeholder, assign a role: Advocate, Supporter, Neutral, or Detractor.</span>,
          <span>Enter their title, relationship to the financial sign-off authority, and their specific concern or advocacy.</span>,
          <span>Note the influence map — who does this person listen to.</span>,
          <span>Flag any unnamed roles. Three named stakeholders minimum before Commit: confirmed advocate, named financial sign-off authority, and at least one other meeting participant.</span>,
        ]}
      />

      <Callout>
        <strong style={{ color: BLACK }}>Contact-mapping rule.</strong> No account moves into
        Commit with fewer than three named stakeholders mapped. One-contact accounts in Commit are
        fragile — advocates leave companies or get overridden.
      </Callout>

      {/* ── Chapter 9 — Plans and Audits ─────────────────────────────────────── */}
      <ManualSectionHeading id="plans-audits" num={9} title="Plans, audits, and the Attilius scoring model" />

      <ManualPara>
        Route — <code>/#/account/:id</code> (tab: Plan for milestone plans; tab: Coaching for
        Attilius audit). Also: <code>/#/accounts</code> for portfolio-level audit.
      </ManualPara>

      <ManualTable
        headers={["Score range", "Typical status", "Meaning"]}
        rows={[
          ["0–20", "Suspect", "Insufficient qualification. Not forecastable."],
          ["21–40", "Prospect", "CP1 and CP2 answered. In BDR handoff range."],
          ["41–60", "Qualified Opp", "CP3–CP5 in progress. AE working the deal. Best Case or Upside."],
          ["61–80", "Full Opportunity", "CP5–CP7 substantially answered. Upside or Commit."],
          ["81–100", "Commit / Close", "CP8–CP9 answered. Disqualifier checkpoints clear. Commit or WON."],
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Running a plan audit — every Friday
      </h3>

      <StepList
        steps={[
          <span>Open <code>/#/accounts</code>. Sort by composite score descending.</span>,
          <span>For each Top 10 account, compare current score versus target score for this week in the milestone plan.</span>,
          <span>For any account behind plan: open the detail. Read "Where We're Stuck." Identify the specific gap preventing progression.</span>,
          <span>Set a documented action item — a specific call, question to answer, or stakeholder to reach. Enter this as a next action in the activity log.</span>,
          <span>For any account ahead of plan: verify the score reflects real evidence. Over-scoring is as dangerous as under-scoring — it creates false Commit and collapses at close.</span>,
        ]}
      />

      <ManualPara>
        The Coaching tab on each account detail is Attilius's account-level advisory. It tells you:
        what evidence gap is most likely to kill this deal, what the next highest-leverage action is,
        and how the account's progression compares to the median at this stage. Read it before every
        executive meeting or stakeholder call on a Top 10 account.
      </ManualPara>

      {/* ── Chapter 10 — Plan vs Actual ───────────────────────────────────────── */}
      <ManualSectionHeading id="plan-vs-actual" num={10} title="Plan vs actual: pipeline coverage, win-rate, and the leaderboard" />

      <ManualPara>
        Route — <code>/#/plan-vs-actual</code> (top nav: PLAN VS ACTUAL).
      </ManualPara>

      <ManualImage
        src={shot06}
        alt="Plan vs Actual Q2 2026 — team attainment by AE, pipeline coverage, and leaderboard"
        caption="Live screen: /#/plan-vs-actual — team attainment by AE, pipeline coverage, and leaderboard"
      />

      <StepList
        steps={[
          <span>Open <code>/#/plan-vs-actual</code>. Default view is Quarter.</span>,
          <span>Read the dark stat strip first — Overall Attainment, Closed Quarter, Weighted Pipeline, Gap to Plan. Four numbers, read left to right.</span>,
          <span>Read the four KPI cards: Quota Attainment (your personal closed-won vs individual target), Pipeline Coverage (weighted pipeline as multiple of remaining gap), Deals Closed (count of closed-won this quarter), MQL Volume (marketing-to-BDR handoff feeding your pipeline).</span>,
          <span>Read the By-AE table. Color coding: Green (≥95%), Yellow (80–94%), Orange (60–79%), Red (&lt;60%).</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Pipeline coverage formula
      </h3>
      <ManualPara>
        Weighted Pipeline ÷ (Quota Target − Closed Won to Date) = Coverage Multiple. The target is 5x in early quarter, maintaining 3x through late quarter.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Weekly rhythm for an AE
      </h3>

      <ManualTable
        headers={["Day", "What to do"]}
        rows={[
          ["Monday", "Open /#/today. Read the Header quota strip. Read the First Hour Directive — that is your first call. Scan the Cadence Queue for overdue steps. Review Top 10 in the Sales Plan workbook — current checkpoint, firm timeline, next action. Check pipeline coverage — if below 3x, identify accounts to add or advance."],
          ["Tue–Thu", "Execute discovery calls, qualification conversations, and stakeholder meetings. Log activity daily. Update Opportunity DNA after every substantive conversation — do not batch updates."],
          ["Friday", "Pipeline review prep: for each Top 10 account, state current checkpoint, forecast bucket, firm timeline, and next action in 60 seconds. Plan vs Actual check-in."],
        ]}
      />

      {/* ── Chapter 11 — Glossary ────────────────────────────────────────────── */}
      <ManualSectionHeading id="glossary" num={11} title="Glossary, common gotchas, and closing note" />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M-PACT</strong> — the Mi6 operating model. M = Methodology. P = Process / Prioritization. A = Accountability. C = Cadence / Coaching. T = Timing / Timeline management.</span>,
          <span><strong style={{ color: BLACK }}>Firm / formal timeline</strong> — the buyer-stated date a project must close by. Always lives under CP4 — Timeline. Never a standalone field.</span>,
          <span><strong style={{ color: BLACK }}>Compelling Reason to Buy</strong> — the business case that makes the decision urgent for the prospect. Distinct from product features. Must be grounded in the prospect's named pain and stated timeline.</span>,
          <span><strong style={{ color: BLACK }}>Helix</strong> — the Attilius diagnostic visualization. As an AE, you drive the helix progression on every account in your pipeline.</span>,
          <span><strong style={{ color: BLACK }}>Attilius</strong> — the intelligence layer. The elephant mark. Generates the Morning Brief, coaching nudges, and account-level "What Advances This" cards.</span>,
          <span><strong style={{ color: BLACK }}>Two-mark rule</strong> — Attilius is the elephant. The helix is the diagnostic. Do not mix the two marks.</span>,
          <span><strong style={{ color: BLACK }}>Top 10 ABM</strong> — the ten named accounts that constitute the AE's plan for the year. Not every account in the territory. Ten named, qualified, milestone-planned accounts.</span>,
          <span><strong style={{ color: BLACK }}>Composite score</strong> — the 0–100 weighted score derived from CP answers under the M-PACT framework. Generated from answered questions in the Opportunity DNA tab.</span>,
          <span><strong style={{ color: BLACK }}>Disqualifier flag</strong> — a red answer on a disqualifier checkpoint that signals a blocking condition. Accounts with active disqualifier flags should not be in Commit.</span>,
          <span><strong style={{ color: BLACK }}>Forecast buckets (current canon)</strong> — WON (100%), COMMIT (90%), UPSIDE (60%), OMIT (0%). PROBABLE was retired in W63/W64 and is no longer a valid bucket.</span>,
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Common gotchas.</strong> Marking CP4 — Timeline green without a documented firm timeline invalidates Commit. Advancing forecast bucket ahead of checkpoint score will be caught in every pipeline review. One-contact accounts in Commit are fragile. Accounts stale for 45+ days without checkpoint advancement need intervention or omission. "Fiscal year urgency" is not a firm timeline — a specific prospect-stated date is. Read the BDR handoff notes before every first meeting. PROBABLE is not a forecast bucket — use UPSIDE for deals with unconfirmed timelines.
      </Callout>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Closing note
      </h3>
      <ManualPara>
        You own the revenue. The BDR sets the table. Marketing fills the top of the funnel. The
        Manager creates the system. But the revenue closes because of what you do in the room, what
        questions you ask, and how honestly you manage the pipeline you commit to. Run the
        checkpoints with discipline, keep the firm timeline alive on every Top 10 account, and
        do not forecast what you cannot support with evidence. Do the work, and the work will show.
      </ManualPara>

      <Callout>
        <strong style={{ color: BLACK }}>Closing note.</strong> The helix is the diagnostic.
        The path is the play.
      </Callout>
    </ManualLayout>
  );
}
